import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FaTimes, FaPaperPlane, FaArrowLeft, FaChevronRight, FaCar, FaFileContract, FaUsers, FaMoneyBillWave, FaWrench, FaFileInvoiceDollar, FaCreditCard, FaChartBar, FaCog, FaRocket, FaSearch, FaWhatsapp, FaExternalLinkAlt, FaQuestionCircle } from 'react-icons/fa';
import { BiHomeAlt } from 'react-icons/bi';
import styled, { keyframes } from 'styled-components';
import { searchKnowledge, categories, knowledgeBase, KnowledgeEntry, Category } from './knowledge-base';
import { IconType } from 'react-icons';
import logoIA from '../../assets/logo-ia.png';
import { useAIChat } from '../../hooks/useAIChat';
import { WHATSAPP_SUPORTE_URL } from '../../services/constantes';
import { rastrearEventoFunil } from '../../services/rastreamentoFunil';
import { AbaAssistente, EVENTO_ABRIR_ASSISTENTE, EVENTO_ALTERNAR_ASSISTENTE, useInsights } from '../assistente/InsightsProvider';
import { InsightCard } from '../assistente/InsightCard';
import { PreferenciasAssistente } from '../assistente/PreferenciasAssistente';

const categoryIcons: Record<string, IconType> = {
  inicio: FaRocket,
  veiculos: FaCar,
  locacao: FaFileContract,
  locatarios: FaUsers,
  financeiro: FaMoneyBillWave,
  manutencao: FaWrench,
  notas: FaFileInvoiceDollar,
  planos: FaCreditCard,
  relatorios: FaChartBar,
  config: FaCog,
};

// IDs das perguntas frequentes exibidas no menu principal
const FAQ_IDS = ['locatarios-4', 'locacao-3', 'financeiro-1', 'manutencao-1', 'locacao-6', 'financeiro-8', 'financeiro-9'];

// ===== STYLES =====
const Window = styled.div`
  position: fixed;
  bottom: 98px;
  right: 24px;
  width: 460px;
  max-width: calc(100vw - 32px);
  height: 680px;
  max-height: calc(100vh - 120px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 480px) {
    bottom: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const TopBar = styled.div`
  background: linear-gradient(135deg, #1F467E 0%, #2d6bc4 100%);
  color: white;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const TopBarLogo = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const BackButton = styled.button`
  background: rgba(255,255,255,0.12);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.22); }
`;

const CloseBtn = styled(BackButton)``;

const TopBarTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
`;

const TopBarSub = styled.div`
  font-size: 0.72rem;
  opacity: 0.65;
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
`;

const HomeContent = styled.div`
  padding: 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 14px 0 6px;
`;

const CategoryItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 10px;
  border-radius: 10px;
  transition: background 0.1s;
  width: 100%;
  text-align: left;

  &:hover {
    background: #f5f7fa;
  }

  .icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #f0f4f8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1F467E;
    font-size: 0.88rem;
    flex-shrink: 0;
  }

  .label {
    flex: 1;
    font-size: 0.86rem;
    font-weight: 500;
    color: #1f2937;
  }

  .chevron {
    color: #d1d5db;
    font-size: 0.6rem;
  }
`;

const FaqItem = styled.button`
  background: #f8fafc;
  border: 1px solid #e8ecf1;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  transition: all 0.12s;
  width: 100%;
  text-align: left;

  &:hover {
    background: #eef2f7;
    border-color: #cdd5e0;
  }

  .faq-icon {
    color: #94a3b8;
    font-size: 0.72rem;
    flex-shrink: 0;
  }

  .faq-label {
    flex: 1;
    font-size: 0.82rem;
    font-weight: 500;
    color: #475569;
  }

  .chevron {
    color: #cbd5e1;
    font-size: 0.5rem;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 11px 14px 11px 38px;
  font-size: 0.85rem;
  outline: none;
  background: #f9fafb;
  transition: all 0.12s;

  &:focus {
    border-color: #1F467E;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(31, 70, 126, 0.08);
  }

  &::placeholder { color: #9ca3af; }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 0.78rem;
  pointer-events: none;
`;

const QuestionsList = styled.div`
  padding: 14px 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0 12px;

  .icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f0f4f8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1F467E;
    font-size: 0.82rem;
  }

  .title {
    font-size: 0.92rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

const QuestionItem = styled.button`
  background: none;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  text-align: left;
  font-size: 0.84rem;
  color: #374151;
  padding: 13px 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: color 0.1s;
  width: 100%;

  &:last-child { border-bottom: none; }

  &:hover {
    color: #1F467E;
  }

  .chevron {
    color: #d1d5db;
    font-size: 0.55rem;
    flex-shrink: 0;
  }

  &:hover .chevron { color: #1F467E; }
`;

const ChatArea = styled.div`
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BotMsg = styled.div`
  background: #f3f4f6;
  border-radius: 12px 12px 12px 2px;
  padding: 13px 16px;
  font-size: 0.84rem;
  color: #1f2937;
  line-height: 1.6;
  max-width: 92%;
  white-space: pre-wrap;
  word-wrap: break-word;

  strong, b { color: #1F467E; font-weight: 600; }
`;

const UserMsg = styled.div`
  background: #1F467E;
  color: white;
  border-radius: 12px 12px 2px 12px;
  padding: 11px 16px;
  font-size: 0.84rem;
  line-height: 1.45;
  max-width: 85%;
  align-self: flex-end;
`;

const SuggestedActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 4px;
`;

const ActionChip = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  padding: 7px 15px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #1F467E;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: #1F467E;
    color: white;
    border-color: #1F467E;
  }
`;

const Footer = styled.div`
  padding: 14px 18px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  background: #fafbfc;
`;

const MsgInput = styled.input`
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 0.92rem;
  outline: none;
  background: #fff;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    border-color: #1F467E;
    box-shadow: 0 0 0 3px rgba(31, 70, 126, 0.08);
  }
  &::placeholder { color: #9ca3af; font-size: 0.9rem; }
`;

const SendButton = styled.button`
  background: #1F467E;
  border: none;
  color: white;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s;
  flex-shrink: 0;

  &:hover { background: #17355F; }
  &:disabled { background: #cbd5e1; cursor: not-allowed; }
`;

const WhatsAppChip = styled(ActionChip)`
  background: #25d366;
  color: #fff;
  border-color: #25d366;
  text-decoration: none;

  &:hover {
    background: #1eba59;
    color: #fff;
    border-color: #1eba59;
  }
`;

const typingDots = keyframes`
  0%, 20% { opacity: 0.2; }
  50% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const TypingIndicator = styled.div`
  background: #f3f4f6;
  border-radius: 12px 12px 12px 2px;
  padding: 13px 16px;
  font-size: 0.84rem;
  color: #9ca3af;
  max-width: 92%;
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    animation: ${typingDots} 1.4s infinite;
    font-size: 1.2rem;
    line-height: 1;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

const Abas = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
`;

const Aba = styled.button<{ $ativa: boolean }>`
  flex: 1;
  background: none;
  border: 0;
  border-bottom: 2px solid ${({ $ativa }) => ($ativa ? '#1F467E' : 'transparent')};
  color: ${({ $ativa }) => ($ativa ? '#1F467E' : '#64748b')};
  font-weight: ${({ $ativa }) => ($ativa ? 600 : 500)};
  font-size: 0.84rem;
  padding: 12px 0 10px;
  cursor: pointer;
`;

const ListaInsights = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8fafc;
  min-height: 100%;
  box-sizing: border-box;
`;

const BarraInsights = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: -4px;
`;

const LinkPreferencias = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: #1F467E;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
`;

const SemInsights = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 0.84rem;
  padding: 48px 16px;
`;

const RestantesInfo = styled.div`
  text-align: center;
  padding: 4px 0 8px;
  font-size: 0.65rem;
  color: #9ca3af;
`;

// ===== TYPES =====
interface Message {
  type: 'bot' | 'user';
  text: string;
  entry?: KnowledgeEntry;
}

// ===== COMPONENT =====
export const FloatingHelpBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setLocalMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'home' | 'chat' | 'category'>('home');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [aba, setAba] = useState<AbaAssistente>('ajuda');
  const [verPreferencias, setVerPreferencias] = useState(false);
  const insights = useInsights();
  const mostrarInsights = insights.habilitado;
  const abaAtual: AbaAssistente = mostrarInsights ? aba : 'ajuda';
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const aiChat = useAIChat();
  const endRef = useRef<HTMLDivElement>(null);

  const faqEntries = FAQ_IDS.map(id => knowledgeBase.find(e => e.id === id)).filter(Boolean) as KnowledgeEntry[];

  const isTyping = aiChat.isTyping;
  const restantes = aiChat.restantes;

  // Sincroniza mensagens da IA no array unificado
  const prevAiCountRef = useRef(0);
  useEffect(() => {
    const aiMsgs = aiChat.messages;
    if (aiMsgs.length > prevAiCountRef.current) {
      const newMsgs = aiMsgs.slice(prevAiCountRef.current);
      setLocalMessages(prev => [...prev, ...newMsgs.map(m => ({ ...m } as Message))]);
    }
    prevAiCountRef.current = aiMsgs.length;
  }, [aiChat.messages]);

  // Scroll automático
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && view === 'chat') setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen, view]);

  const abrir = useCallback((abaPedida: AbaAssistente) => {
    setAba(abaPedida);
    setIsOpen(true);
    rastrearEventoFunil('assistente_painel_aberto', { aba: abaPedida });
  }, []);

  // Abertura por evento: o botao do header e a pagina de ajuda abrem na Ajuda ('open-help-bot'); o card do
  // dashboard abre na aba pedida; o avatar do canto alterna a aba Insights.
  const estadoRef = useRef({ isOpen, aba });
  estadoRef.current = { isOpen, aba };
  useEffect(() => {
    const abrirAjuda = () => abrir('ajuda');
    const abrirAba = (e: Event) => abrir((e as CustomEvent<{ aba?: AbaAssistente }>).detail?.aba ?? 'insights');
    const alternar = () => {
      const { isOpen: aberto, aba: abaAberta } = estadoRef.current;
      if (aberto && abaAberta === 'insights') setIsOpen(false);
      else abrir('insights');
    };
    window.addEventListener('open-help-bot', abrirAjuda);
    window.addEventListener(EVENTO_ABRIR_ASSISTENTE, abrirAba);
    window.addEventListener(EVENTO_ALTERNAR_ASSISTENTE, alternar);
    return () => {
      window.removeEventListener('open-help-bot', abrirAjuda);
      window.removeEventListener(EVENTO_ABRIR_ASSISTENTE, abrirAba);
      window.removeEventListener(EVENTO_ALTERNAR_ASSISTENTE, alternar);
    };
  }, [abrir]);

  const filteredCategories = searchTerm.trim()
    ? categories.filter(c => {
        const entries = knowledgeBase.filter(e => e.category === c.id);
        const term = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return c.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term) ||
          entries.some(e =>
            e.question.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(term) ||
            e.keywords.some(k => k.includes(term))
          );
      })
    : categories;

  const ask = (entry: KnowledgeEntry) => {
    setLocalMessages(prev => [
      ...prev,
      { type: 'user', text: entry.question },
      { type: 'bot', text: entry.answer, entry },
    ]);
    setView('chat');
  };

  const send = () => {
    const q = input.trim();
    if (!q || isTyping) return;
    setInput('');
    setAba('ajuda');
    setView('chat');

    // Tenta knowledge base local primeiro (instantâneo, sem custo)
    const results = searchKnowledge(q);
    if (results.length > 0) {
      const kbMsgs: Message[] = [
        { type: 'user', text: q },
        { type: 'bot', text: results[0].answer, entry: results[0] },
      ];
      if (results.length > 1) {
        kbMsgs.push({
          type: 'bot',
          text: 'Perguntas relacionadas:\n' + results.slice(1, 4).map((r, i) => `${i + 1}. ${r.question}`).join('\n'),
        });
      }
      setLocalMessages(prev => [...prev, ...kbMsgs]);
      return;
    }

    // Fallback: chama IA
    aiChat.sendMessage(q);
  };

  const goTo = (route: string) => { history.push(route); setIsOpen(false); };

  const fmt = (text: string) =>
    text.split('\n').map((line, i, arr) => (
      <span key={i}>
        {line.split(/\*\*(.*?)\*\*/g).map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
        {i < arr.length - 1 && <br />}
      </span>
    ));

  const catEntries = selectedCategory ? knowledgeBase.filter(e => e.category === selectedCategory) : [];
  const selCat = categories.find(c => c.id === selectedCategory);
  const SelIcon = selectedCategory ? categoryIcons[selectedCategory] : null;

  // O assistente vive apenas no header; aqui so renderiza a janela quando aberta
  // (aberta pelo botao do header ou pela pagina de ajuda via evento 'open-help-bot').
  return (
    <>
      {isOpen && (
        <Window>
          <TopBar>
            <TopBarLeft>
              {abaAtual === 'ajuda' && view !== 'home' && (
                <BackButton onClick={() => {
                  if (view === 'category') { setView('home'); setSelectedCategory(null); }
                  else setView('home');
                }}>
                  <FaArrowLeft size={12} />
                </BackButton>
              )}
              {(abaAtual === 'insights' || view === 'home') && <TopBarLogo src={logoIA} alt="LocaSmartPro" />}
              <div>
                <TopBarTitle>Assistente LocaSmartPro</TopBarTitle>
                <TopBarSub>{abaAtual === 'insights' ? 'O que notei na sua conta' : 'Como posso te ajudar?'}</TopBarSub>
              </div>
            </TopBarLeft>
            <CloseBtn onClick={() => setIsOpen(false)}>
              <FaTimes size={12} />
            </CloseBtn>
          </TopBar>

          {mostrarInsights && (
            <Abas role="tablist">
              <Aba type="button" role="tab" aria-selected={abaAtual === 'insights'} $ativa={abaAtual === 'insights'} onClick={() => setAba('insights')}>
                Insights{insights.itens.length > 0 ? ` (${insights.itens.length})` : ''}
              </Aba>
              <Aba type="button" role="tab" aria-selected={abaAtual === 'ajuda'} $ativa={abaAtual === 'ajuda'} onClick={() => setAba('ajuda')}>
                Ajuda
              </Aba>
            </Abas>
          )}

          <Content>
            {/* INSIGHTS */}
            {abaAtual === 'insights' && verPreferencias && (
              <PreferenciasAssistente aoVoltar={() => setVerPreferencias(false)} />
            )}

            {abaAtual === 'insights' && !verPreferencias && (
              <ListaInsights>
                <BarraInsights>
                  <LinkPreferencias type="button" onClick={() => setVerPreferencias(true)}>Preferências</LinkPreferencias>
                </BarraInsights>
                {insights.itens.map((item, i) => (
                  <InsightCard key={item.id} item={item} superficie="painel" posicao={i} aoNavegar={() => setIsOpen(false)} />
                ))}
                {insights.itens.length === 0 && (
                  <SemInsights>Tudo em dia por aqui. Volto quando notar algo novo.</SemInsights>
                )}
              </ListaInsights>
            )}

            {/* HOME */}
            {abaAtual === 'ajuda' && view === 'home' && (
              <HomeContent>
                <SearchWrap>
                  <SearchIcon><FaSearch /></SearchIcon>
                  <SearchInput
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar por tema..."
                    onKeyDown={e => {
                      if (e.key === 'Enter' && searchTerm.trim()) {
                        const results = searchKnowledge(searchTerm);
                        if (results.length > 0) ask(results[0]);
                      }
                    }}
                  />
                </SearchWrap>

                {!searchTerm.trim() && (
                  <>
                    <SectionLabel>Perguntas Frequentes</SectionLabel>
                    {faqEntries.map(entry => (
                      <FaqItem key={entry.id} onClick={() => ask(entry)}>
                        <FaQuestionCircle className="faq-icon" />
                        <span className="faq-label">{entry.question}</span>
                        <FaChevronRight className="chevron" />
                      </FaqItem>
                    ))}
                  </>
                )}

                <SectionLabel>Temas</SectionLabel>

                {filteredCategories.map(cat => {
                  const Icon = categoryIcons[cat.id] || FaRocket;
                  return (
                    <CategoryItem key={cat.id} onClick={() => { setSelectedCategory(cat.id); setView('category'); }}>
                      <div className="icon-wrap"><Icon /></div>
                      <span className="label">{cat.label}</span>
                      <FaChevronRight className="chevron" />
                    </CategoryItem>
                  );
                })}

                {filteredCategories.length === 0 && (
                  <div style={{ padding: '16px 0', textAlign: 'center', color: '#9ca3af', fontSize: '0.84rem' }}>
                    Nenhum tema encontrado
                  </div>
                )}

                <SectionLabel>Fale Conosco</SectionLabel>
                <CategoryItem
                  as="a"
                  href={WHATSAPP_SUPORTE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="icon-wrap" style={{ background: '#dcfce7', color: '#25d366' }}>
                    <FaWhatsapp />
                  </div>
                  <span className="label">Falar com a Equipe</span>
                  <FaChevronRight className="chevron" />
                </CategoryItem>
              </HomeContent>
            )}

            {/* CATEGORY */}
            {abaAtual === 'ajuda' && view === 'category' && selCat && (
              <QuestionsList>
                <CatHeader>
                  {SelIcon && <div className="icon-wrap"><SelIcon /></div>}
                  <div className="title">{selCat.label}</div>
                </CatHeader>
                {catEntries.map(entry => (
                  <QuestionItem key={entry.id} onClick={() => ask(entry)}>
                    {entry.question}
                    <FaChevronRight className="chevron" />
                  </QuestionItem>
                ))}
              </QuestionsList>
            )}

            {/* CHAT */}
            {abaAtual === 'ajuda' && view === 'chat' && (
              <ChatArea>
                {messages.map((msg, i) => (
                  <React.Fragment key={i}>
                    {msg.type === 'user' ? (
                      <UserMsg>{msg.text}</UserMsg>
                    ) : (
                      <>
                        <BotMsg>{fmt(msg.text)}</BotMsg>
                        {(msg.entry?.route || msg.entry?.links?.length || i === messages.length - 1) && (
                          <SuggestedActions>
                            {msg.entry?.route && (
                              <ActionChip onClick={() => goTo(msg.entry!.route!)}>
                                <FaExternalLinkAlt size={10} />
                                Ir para a pagina
                              </ActionChip>
                            )}
                            {msg.entry?.links?.map((link, li) => (
                              <ActionChip
                                key={li}
                                as="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none' }}
                              >
                                <FaExternalLinkAlt size={10} />
                                {link.label}
                              </ActionChip>
                            ))}
                            <ActionChip onClick={() => { setView('home'); setSelectedCategory(null); }}>
                              <BiHomeAlt size={12} />
                              Voltar ao menu
                            </ActionChip>
                            <WhatsAppChip
                              as="a"
                              href={WHATSAPP_SUPORTE_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FaWhatsapp size={12} />
                              Falar com Suporte Humano
                            </WhatsAppChip>
                          </SuggestedActions>
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
                {isTyping && (
                  <TypingIndicator>
                    <span>.</span><span>.</span><span>.</span>
                  </TypingIndicator>
                )}
                <div ref={endRef} />
              </ChatArea>
            )}
          </Content>

          <Footer>
            <MsgInput
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder="Pergunte o que quiser..."
            />
            <SendButton onClick={send} disabled={!input.trim() || isTyping}>
              <FaPaperPlane size={13} />
            </SendButton>
          </Footer>
          {restantes !== null && (
            <RestantesInfo>
              {restantes} pergunta{restantes !== 1 ? 's' : ''} restante{restantes !== 1 ? 's' : ''} hoje
            </RestantesInfo>
          )}
        </Window>
      )}
    </>
  );
};
